// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import {
  FORMID,
  TestRunParams,
  getStepStyle,
} from "../TestRun/TestRunConstants";
import { ROUTE_DEFINITIONS, getPath } from "routes/routes";
import React, { memo, useCallback, useEffect } from "react";
import { updateUserInterpretation, useTestRunState } from "store/testrun";
import { useHistory, useParams } from "react-router-dom";

import Asset from "components/ui/Asset";
import QuestionnaireForm from "../ui/Questionnaire/Questionnaire";
import QuestionnaireInput from "components/ui/Questionnaire/QuestionnaireInput";
import { confirmAlert } from "../../utils/confirmAlert";
import { setStepReady } from "components/TestRun/TestRunStepReadyEmitter";
import { useAppDispatch } from "store";
import { useAuthState } from "../../store/auth";
import { useTestRunConfig } from "components/TestRun/useTestRunConfig";
import { useTheme } from "@material-ui/core";

const blueLineOptions = [
  { value: "Yes", name: "Yes" },
  { value: "No", name: "No" },
];

const questionValidator = (text: string | null): string | undefined => {
  if (!text || text === "") {
    return "Must enter a value";
  }
  return blueLineOptions.some(option => text === option.value)
    ? undefined
    : "Select an option";
};

const WhatDoYouSeePanel = memo(() => {
  // TODO: get questionaire based on the type of strip selected
  const classes = getStepStyle();
  const { getNextDefaultStep, getNextStepOfType } = useTestRunConfig();
  const { step, testRunUID } = useParams<TestRunParams>();
  const QuestionnaireFormMemoized = memo(QuestionnaireForm);
  const history = useHistory();
  const { auth } = useAuthState();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const testRunState = useTestRunState();

  const stepReadyCallback = useCallback(
    (stepReady: boolean) => {
      setStepReady(step, stepReady);
    },
    [step]
  );

  const previousResult =
    testRunState?.testRun?.userInterpretation?.blueLine || "";

  // If already recorded, enable "next" button
  useEffect(() => {
    if (previousResult) {
      stepReadyCallback(true);
    }
  }, [previousResult, stepReadyCallback]);

  // TODO: Generic Error handler.
  if (!auth) {
    return <div>Oops, something went wrong</div>;
  }

  if (!testRunState.subscribed) {
    return <div />; // Add Loader
  }

  return (
    <>
      <p>
        Look at the test strip with the arrows pointing down towards you to
        answer the following questions.
      </p>
      <Asset
        height={180}
        width={290}
        className={classes.centeredAsset}
        alt="Image of the test strip after removal from the tube."
        src="lookAtTestStrip.png"
      />
      <QuestionnaireFormMemoized
        formId={FORMID}
        onFormReady={stepReadyCallback}
        onSubmit={async (event, questions) => {
          try {
            event.preventDefault();

            const blueLineVal = questions["blueLine"].value;
            await dispatch(
              updateUserInterpretation({
                userInterpretation: {
                  blueLine: blueLineVal,
                },
              })
            );

            // Resets the state.
            stepReadyCallback(false);
            const nextStep =
              blueLineVal === "Yes"
                ? getNextDefaultStep({
                    currentStepName: step!,
                  })
                : getNextStepOfType({
                    currentStepName: step!,
                    nextStepType: "noLine",
                  });
            const path = getPath(ROUTE_DEFINITIONS.PERFORMTEST, {
              params: {
                ":testRunUID": testRunUID,
                ":step": nextStep!,
              },
            });
            history.push(path);
          } catch (e) {
            confirmAlert({ title: "oops", message: e.message, theme });
          }
        }}
      >
        <QuestionnaireInput
          label="Do you see a blue line in the middle of the test strip?"
          mandatory={true}
          name="blueLine"
          placeholder="Choose an option"
          type="buttongroup"
          validators={[questionValidator]}
          selectOptions={blueLineOptions}
          value={previousResult}
        />
      </QuestionnaireFormMemoized>
    </>
  );
});

export default WhatDoYouSeePanel;
